import { ReactNode } from 'react';
import { Card } from '../ui';

export type Tone = 'amber' | 'sky' | 'emerald' | 'rose' | 'violet' | 'slate' | 'orange' | 'red';

const TONE_TEXT: Record<Tone, string> = {
  amber: 'text-amber-400',
  sky: 'text-sky-400',
  emerald: 'text-emerald-400',
  rose: 'text-rose-400',
  violet: 'text-violet-300',
  slate: 'text-slate-300',
  orange: 'text-orange-400',
  red: 'text-red-400',
};

const TONE_CHIP: Record<Tone, string> = {
  amber: 'bg-amber-500/15 text-amber-400 border-amber-500/40',
  sky: 'bg-sky-500/15 text-sky-400 border-sky-500/40',
  emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40',
  rose: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  violet: 'bg-violet-500/15 text-violet-300 border-violet-500/40',
  slate: 'bg-slate-800 text-slate-200 border-slate-700',
  orange: 'bg-orange-500/15 text-orange-400 border-orange-500/40',
  red: 'bg-red-500/15 text-red-400 border-red-500/40',
};

export interface StatItem {
  label: string;
  /** Already formatted (number, string, or ReactNode). */
  value: ReactNode;
  tone?: Tone;
}

/**
 * Grille de cartes de stats partagée entre Relances Client et Relances Factures.
 * 2 cols mobile → 3 cols tablette → autant que d'items en desktop (max 6).
 */
export function RelanceStats({ items }: { items: StatItem[] }) {
  const cols = Math.min(items.length, 6);
  // Tailwind ne génère pas dynamiquement les classes lg:grid-cols-N : on les liste.
  const lgCols: Record<number, string> = {
    1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5', 6: 'lg:grid-cols-6',
  };
  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 ${lgCols[cols]} gap-3 mb-6`}>
      {items.map((s, i) => (
        <Card key={i} className="p-4">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">{s.label}</div>
          <div className={`font-heading text-2xl ${TONE_TEXT[s.tone || 'amber']}`}>{s.value}</div>
        </Card>
      ))}
    </div>
  );
}

export interface FilterItem {
  key: string;
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  tone?: Tone;
}

/**
 * Barre de filtres à puces partagée. Toujours placée juste sous les stats,
 * jamais en bas de page.
 */
export function RelanceFilterChips({ items }: { items: FilterItem[] }) {
  return (
    <div className="flex gap-2 mb-5 flex-wrap">
      {items.map((it) => {
        const tone = it.tone || 'amber';
        const activeCls = TONE_CHIP[tone];
        return (
          <button
            key={it.key}
            type="button"
            onClick={it.onClick}
            className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
              it.active ? activeCls : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:bg-slate-800/60 hover:text-slate-200'
            }`}
          >
            {it.label}{typeof it.count === 'number' ? ` (${it.count})` : ''}
          </button>
        );
      })}
    </div>
  );
}
